import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../config/axios';
import { useCart, CartItem } from '../contexts/CartContext';
import { formatPickupWindow } from '../utils/dateTimeUtils';
import { getVatBreakdown } from '../utils/vatUtils';
import { useTranslation } from '../hooks/useTranslation';

interface PickupDate {
  id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  last_order_datetime: string | null;
  location_name: string;
  location_address: string;
}

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  address?: string;
}

interface CreateOrderRequest {
  pickup_date_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  terms_accepted: boolean;
  items: {
    product_id: string;
    quantity_kg: number;
  }[];
}

interface Order {
  id: string;
  pickup_date_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  total_amount: number;
  status: string;
  is_paid: boolean;
  created_at: string;
  paid_at: string | null;
  items: {
    id: string;
    product_id: string;
    quantity_kg: number;
    price_per_kg: number;
    subtotal: number;
    product: {
      id: string;
      name: string;
    };
  }[];
  pickup_date: PickupDate;
}

// Declare Stripe on window
declare global {
  interface Window {
    Stripe: any;
  }
}

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const { t, language } = useTranslation();
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [createdOrders, setCreatedOrders] = useState<Order[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutMounted, setCheckoutMounted] = useState(false);

  useEffect(() => {
    // Load data from sessionStorage
    const storedCustomerInfo = sessionStorage.getItem('customerInfo');
    const storedTermsAccepted = sessionStorage.getItem('termsAccepted');

    if (!storedCustomerInfo || !storedTermsAccepted || cart.length === 0) {
      navigate('/cart');
      return;
    }

    setCustomerInfo(JSON.parse(storedCustomerInfo));
    setTermsAccepted(JSON.parse(storedTermsAccepted));
  }, [navigate, cart]);

  const handlePayment = async () => {
    if (!customerInfo || cart.length === 0) return;

    setIsProcessing(true);

    try {
      // Group cart items by pickup date
      const groupedCart = cart.reduce((acc, item) => {
        const key = item.pickup_date_id;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {} as Record<string, CartItem[]>);

      // Create orders (one per pickup date)
      const orders: Order[] = [];
      
      for (const [pickupDateId, items] of Object.entries(groupedCart)) {
        const orderRequest: CreateOrderRequest = {
          pickup_date_id: pickupDateId,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
          customer_address: customerInfo.address || "",
          terms_accepted: termsAccepted,
          items: items.map(item => ({
            product_id: item.product_id,
            quantity_kg: item.quantity_kg,
          })),
        };

        const createResponse = await axiosInstance.post('/api/orders', orderRequest);
        orders.push(createResponse.data);
      }

      setCreatedOrders(orders);
      
      // Store order IDs in sessionStorage for the return page
      sessionStorage.setItem('pendingOrderIds', JSON.stringify(orders.map(o => o.id)));
      
      // Initialize Stripe Checkout
      await initializeStripeCheckout(orders.map(o => o.id));
      
    } catch (error: any) {
      alert(`${t('payment.createOrderError')}: ${error.response?.data?.detail || error.message}`);
      setIsProcessing(false);
    }
  };

  const initializeStripeCheckout = async (orderIds: string[]) => {
    try {
      // Load Stripe.js
      const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!stripePublicKey) {
        throw new Error('Stripe public key not configured');
      }

      // Create checkout session
      const response = await axiosInstance.post('/api/stripe/create-checkout-session', {
        order_ids: orderIds,
        locale: language
      });

      const { clientSecret } = response.data;

      // Set mounted state first to render the DOM element
      setCheckoutMounted(true);

      // Wait for next tick to ensure DOM is rendered
      setTimeout(async () => {
        try {
          // Initialize Stripe
          const stripe = window.Stripe(stripePublicKey);

          // Initialize embedded checkout
          const checkout = await stripe.initEmbeddedCheckout({
            clientSecret,
          });

          // Mount checkout
          checkout.mount('#checkout');
        } catch (mountError: any) {
          alert(`${t('payment.paymentInitError')}: ${mountError.message}`);
          setIsProcessing(false);
          setCheckoutMounted(false);
        }
      }, 100);
      
    } catch (error: any) {
      alert(`${t('payment.paymentInitError')}: ${error.response?.data?.detail || error.message}`);
      setIsProcessing(false);
    }
  };

  const calculateTotal = (): number => {
    return cart.reduce((total, item) => total + (item.quantity_kg * item.price_per_kg), 0);
  };

  // Group cart items by pickup date for display
  const groupedCart = cart.reduce((acc, item) => {
    const key = item.pickup_date_id;
    if (!acc[key]) {
      acc[key] = {
        pickup_date_id: item.pickup_date_id,
        pickup_date: item.pickup_date,
        pickup_start_time: item.pickup_start_time,
        pickup_end_time: item.pickup_end_time,
        location_name: item.location_name,
        location_address: item.location_address,
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {} as Record<string, any>);

  if (!customerInfo || cart.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{t('payment.loading')}</p>
      </div>
    );
  }

  // If checkout is mounted, show the Stripe checkout form
  if (checkoutMounted) {
    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <h1>{t('payment.completePayment')}</h1>
        <div id="checkout" style={{ marginTop: '20px' }}>
          {/* Stripe Checkout will be mounted here */}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>{t('payment.title')}</h1>

      <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
        <h2>{t('payment.customerInformation')}</h2>
        <p><strong>{t('payment.name')}:</strong> {customerInfo.name}</p>
        <p><strong>{t('payment.email')}:</strong> {customerInfo.email}</p>
        <p><strong>{t('payment.phone')}:</strong> {customerInfo.phone}</p>
      </div>

      {Object.values(groupedCart).map((group: any) => (
        <div key={group.pickup_date_id} style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
          <h3>{t('payment.pickupDetails')}</h3>
          <p><strong>{t('payment.dateTime')}:</strong><br />
            {formatPickupWindow(group.pickup_date, group.pickup_start_time, group.pickup_end_time, t('common.from'), t('common.to'))}
          </p>
          <p><strong>{t('payment.location')}:</strong><br />
            {group.location_name}
          </p>
          <p style={{ color: '#666' }}>
            {group.location_address}
          </p>

          <h4 style={{ marginTop: '20px' }}>{t('payment.items')}</h4>
          {group.items.map((item: CartItem) => (
            <div key={`${item.product_id}-${item.pickup_date_id}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #ddd' }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{item.product_name}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {item.quantity_kg} kg × €{item.price_per_kg.toFixed(2)}/kg
                </div>
              </div>
              <div style={{ fontWeight: 'bold' }}>
                €{(item.quantity_kg * item.price_per_kg).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      ))}

      <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
        {(() => {
          const total = calculateTotal();
          const vatBreakdown = getVatBreakdown(total);
          return (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '8px',
                fontSize: '16px',
                color: '#666'
              }}>
                <span>{t('payment.subtotalExclVat')}:</span>
                <span>€{vatBreakdown.priceExclVat.toFixed(2)}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '12px',
                fontSize: '16px',
                color: '#666'
              }}>
                <span>{t('payment.vat')}:</span>
                <span>€{vatBreakdown.vatAmount.toFixed(2)}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: '20px', 
                fontWeight: 'bold',
                paddingTop: '12px',
                borderTop: '2px solid #333'
              }}>
                <span>{t('payment.totalAmount')}:</span>
                <span>€{total.toFixed(2)}</span>
              </div>
            </>
          );
        })()}
      </div>

      <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107', marginBottom: '20px' }}>
        <p style={{ margin: 0, fontSize: '14px' }}>
          <strong>{t('payment.reminderTitle')}</strong> {t('payment.reminderText')}
        </p>
      </div>

      <button
        onClick={handlePayment}
        disabled={isProcessing}
        style={{
          width: '100%',
          padding: '15px',
          backgroundColor: isProcessing ? '#ccc' : '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: isProcessing ? 'not-allowed' : 'pointer',
        }}
      >
        {isProcessing ? t('payment.processing') : t('payment.proceedToPayment')}
      </button>

      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <button
          onClick={() => navigate('/checkout')}
          disabled={isProcessing}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: '#007bff',
            border: 'none',
            fontSize: '16px',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            textDecoration: 'underline',
          }}
        >
          {t('payment.backToCheckout')}
        </button>
      </div>
    </div>
  );
};

export default PaymentPage;
